/* A baseline's rows, for any template (designs/product-baselines.md BL20):
   the read-only table, with tabs by the grouping column and that group's
   RACI shown once above it, and the row editor, whose inputs follow each
   column's type. */
import { useMemo, useState, type JSX } from "react";
import type { UserRef } from "../../../../shared/organizer-roles";
import {
  BASELINE_LIMITS,
  cellText,
  groupValueOf,
  raciFor,
  type BaselineContent,
  type Cell,
  type Column,
  type Row,
  type TemplateBody,
} from "../../../../shared/baselines";
import { PersonPicker } from "../../intake/PersonPicker";

const NONE: unique symbol = Symbol("not recorded");

export function Person({ person }: { person: UserRef | null | undefined }): JSX.Element {
  return person ? <>{person.name}</> : <span className="state missing">Not recorded</span>;
}

function CellView({ column, cell }: { column: Column; cell: Cell | undefined }): JSX.Element {
  const text = cellText(cell);
  if (text === "") return column.required ? <span className="state missing">Not recorded</span> : <span className="not-set">—</span>;
  return (
    <>
      {text}
      {cell?.unconfirmed && <span className="state unconfirmed">Unconfirmed</span>}
    </>
  );
}

export function RowsTable({
  template,
  content,
  productName,
}: {
  template: TemplateBody;
  content: BaselineContent;
  productName: string;
}): JSX.Element {
  const grouping = template.grouping;
  const groupColumn = grouping ? template.columns.find((c) => c.key === grouping.columnKey) ?? null : null;
  /* rows whose group cell is empty get a Not recorded tab of their own, so
     nothing approved is hidden (Copilot review of #161) */
  const ungrouped = groupColumn !== null && content.rows.some((r) => groupValueOf(template, r) === null);
  const groups: (string | typeof NONE)[] = [...(groupColumn?.options ?? []), ...(ungrouped ? [NONE as typeof NONE] : [])];
  const [group, setGroup] = useState<string | typeof NONE | null>(groups[0] ?? null);
  const groupValue = group === NONE ? null : group;
  const [filter, setFilter] = useState("");
  const shownColumns = template.columns.filter((c) => c.key !== groupColumn?.key);
  const q = filter.trim().toLowerCase();
  const rows = useMemo(
    () =>
      content.rows.filter(
        (r) =>
          (groupColumn === null || groupValueOf(template, r) === groupValue) &&
          (q === "" || Object.values(r.cells).some((c) => cellText(c).toLowerCase().includes(q))),
      ),
    [content.rows, group, q, template],
  );
  const raci = raciFor(template, content, groupValue, productName);

  return (
    <section className="card baseline-rows">
      <h3>Rows</h3>
      {groupColumn && (
        <div className="about-tabs" role="tablist" aria-label={groupColumn.label}>
          {groups.map((g) => (
            <button
              key={g === NONE ? "" : g}
              className={`about-tab${g === group ? " selected" : ""}`}
              role="tab"
              aria-selected={g === group}
              onClick={() => setGroup(g)}
            >
              {g === NONE ? `${groupColumn!.label} not recorded` : g} (
              {content.rows.filter((r) => groupValueOf(template, r) === (g === NONE ? null : g)).length})
            </button>
          ))}
        </div>
      )}
      <dl className="baseline-raci" data-raci={group === NONE ? "none" : (group ?? "all")}>
        <div>
          <dt>Responsible</dt>
          <dd>{raci.responsible}</dd>
        </div>
        <div>
          <dt>Accountable</dt>
          <dd>
            {raci.accountable ? (
              <>
                {raci.accountable.role.label} · <Person person={raci.accountable.person} />
              </>
            ) : (
              <span className="state missing">Not recorded</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Consulted</dt>
          <dd>{raci.consulted.map((r) => r.label).join(", ") || <span className="not-set">Nobody</span>}</dd>
        </div>
        <div>
          <dt>Informed</dt>
          <dd>
            <span className="state missing">Not recorded</span>
          </dd>
        </div>
      </dl>
      <label className="field baseline-filter">
        <span className="field-label">Filter rows</span>
        <input id="baseline-filter" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </label>
      {rows.length === 0 ? (
        <p className="empty">{content.rows.length === 0 ? "No rows yet." : "No rows match."}</p>
      ) : (
        <div className="dash-table">
          <table className="recs baseline-row-table">
            <thead>
              <tr>
                {shownColumns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-row={r.id}>
                  {shownColumns.map((c, i) => (
                    <td key={c.key}>
                      <CellView column={c} cell={r.cells[c.key]} />
                      {i === 0 && r.note !== null && <div className="baseline-note">{r.note}</div>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ---- editing ---- */

function CellInput({
  column,
  cell,
  onChange,
  label,
}: {
  column: Column;
  cell: Cell | undefined;
  onChange: (cell: Cell) => void;
  label: string;
}): JSX.Element {
  const unconfirmed = cell?.unconfirmed ?? false;
  const set = (value: Cell["value"]): void => onChange({ value, unconfirmed });
  const v = cell?.value ?? null;
  let input: JSX.Element;
  switch (column.type) {
    case "longtext":
      input = <textarea aria-label={label} value={typeof v === "string" ? v : ""} maxLength={BASELINE_LIMITS.longtext} onChange={(e) => set(e.target.value)} />;
      break;
    case "number":
      input = (
        <input
          aria-label={label}
          type="number"
          value={typeof v === "number" ? v : ""}
          onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))}
        />
      );
      break;
    case "date":
      input = <input aria-label={label} type="date" value={typeof v === "string" ? v : ""} onChange={(e) => set(e.target.value || null)} />;
      break;
    case "choice":
      input = (
        <select aria-label={label} value={typeof v === "string" ? v : ""} onChange={(e) => set(e.target.value || null)}>
          <option value="">—</option>
          {column.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
      break;
    case "person":
      input = (
        <PersonPicker
          label={label}
          value={v !== null && typeof v === "object" ? v : null}
          onChange={(p) => set(p)}
        />
      );
      break;
    default:
      input = <input aria-label={label} value={typeof v === "string" ? v : ""} maxLength={BASELINE_LIMITS.text} onChange={(e) => set(e.target.value)} />;
  }
  return (
    <div className="baseline-cell-input">
      {input}
      {column.estimateAllowed && (
        <label className="baseline-estimate">
          <input type="checkbox" checked={unconfirmed} onChange={(e) => onChange({ value: v, unconfirmed: e.target.checked })} /> estimate
        </label>
      )}
    </div>
  );
}

let rowSeq = 0;
const newRowId = (taken: readonly string[]): string => {
  let id: string;
  do id = `row-${Date.now().toString(36)}-${(rowSeq++).toString(36)}`;
  while (taken.includes(id));
  return id;
};

export function RowsEditor({
  template,
  rows,
  onChange,
}: {
  template: TemplateBody;
  rows: Row[];
  onChange: (rows: Row[]) => void;
}): JSX.Element {
  const setRow = (i: number, row: Row): void => onChange(rows.map((r, j) => (j === i ? row : r)));
  return (
    <div className="baseline-rows-editor">
      <div className="dash-table">
        <table className="recs baseline-row-table">
          <thead>
            <tr>
              {template.columns.map((c) => (
                <th key={c.key}>
                  {c.label}
                  {c.required && " *"}
                </th>
              ))}
              <th>Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} data-edit-row={r.id}>
                {template.columns.map((c) => (
                  <td key={c.key}>
                    <CellInput
                      column={c}
                      label={`${c.label}, row ${i + 1}`}
                      cell={r.cells[c.key]}
                      onChange={(cell) => setRow(i, { ...r, cells: { ...r.cells, [c.key]: cell } })}
                    />
                  </td>
                ))}
                <td>
                  <input
                    aria-label={`Note, row ${i + 1}`}
                    value={r.note ?? ""}
                    maxLength={BASELINE_LIMITS.note}
                    onChange={(e) => setRow(i, { ...r, note: e.target.value })}
                  />
                </td>
                <td>
                  <button className="btn ghost small" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        className="btn ghost small"
        onClick={() => onChange([...rows, { id: newRowId(rows.map((r) => r.id)), cells: {}, note: null }])}
      >
        Add row
      </button>
    </div>
  );
}
